import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, COLL } from '../../utils/masterDataStore';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiMapPin, FiSearch, FiX,
  FiHome, FiCheck, FiAlertCircle
} from 'react-icons/fi';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import { clearStockLocationCache } from '../../utils/stockFlowHelper';

const InventoryLocations = () => {
  const { activeCompany } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'locations');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    locationCode: '',
    locationName: '',
    locationType: 'Warehouse',
    address: '',
    city: '',
    contactPerson: '',
    contactPhone: '',
    isDefault: false,
    isActive: true
  });

  useEffect(() => {
    if (activeCompany?.id) {
      fetchLocations();
    }
  }, [activeCompany]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/inventoryLocations`),
        orderBy('locationCode')
      );
      const snapshot = await getDocs(q);
      setLocations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
    setLoading(false);
  };

  const generateLocationCode = () => {
    const maxCode = locations.reduce((max, loc) => {
      const num = parseInt(loc.locationCode.replace('LOC-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `LOC-${String(maxCode + 1).padStart(3, '0')}`;
  };

  const handleAdd = () => {
    setFormData({
      locationCode: generateLocationCode(),
      locationName: '',
      locationType: 'Warehouse',
      address: '',
      city: '',
      contactPerson: '',
      contactPhone: '',
      isDefault: locations.length === 0,
      isActive: true
    });
    setEditingLocation(null);
    setShowModal(true);
  };

  const handleEdit = (location) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setFormData({
      locationCode: location.locationCode,
      locationName: location.locationName,
      locationType: location.locationType || 'Warehouse',
      address: location.address || '',
      city: location.city || '',
      contactPerson: location.contactPerson || '',
      contactPhone: location.contactPhone || '',
      isDefault: location.isDefault || false,
      isActive: location.isActive !== false
    });
    setEditingLocation(location);
    setShowModal(true);
  };

  const handleSave = async () => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.locationName.trim()) {
      alert('Location name is required');
      return;
    }

    try {
      // If setting as default, unset any existing default
      if (formData.isDefault) {
        const currentDefault = locations.find(loc => loc.isDefault && loc.id !== editingLocation?.id);
        if (currentDefault) {
          await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, currentDefault.id), {
            isDefault: false,
            updatedAt: serverTimestamp()
          });
        }
      }

      if (editingLocation) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, editingLocation.id), {
          ...formData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/inventoryLocations`), {
          ...formData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowModal(false);
      // Locations are cached for the transaction forms — push the change out now.
      invalidateMaster(activeCompany.id, COLL.locations);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Failed to save location');
    }
  };

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to save
  useDocumentShortcuts({
    onNew: handleAdd,
    onSave: handleSave,
    viewMode: showModal ? 'form' : 'list',
    saving: false
  });

  const handleDelete = async (location) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (location.isDefault) {
      alert('Cannot delete the default location. Please set another location as default first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${location.locationName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'location', location.id);
        if (hasReferences) { alert(message); return; }
        await deleteDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, location.id));
        invalidateMaster(activeCompany.id, COLL.locations);
        fetchLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Failed to delete location');
      }
    }
  };

  const handleSetDefault = async (location) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    try {
      // Unset current default
      const currentDefault = locations.find(loc => loc.isDefault);
      if (currentDefault) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, currentDefault.id), {
          isDefault: false,
          updatedAt: serverTimestamp()
        });
      }

      // Set new default
      await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, location.id), {
        isDefault: true,
        updatedAt: serverTimestamp()
      });

      invalidateMaster(activeCompany.id, COLL.locations);
      fetchLocations();
    } catch (error) {
      console.error('Error setting default:', error);
      alert('Failed to set default location');
    }
  };

  const toggleActive = async (location) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (location.isDefault && location.isActive) {
      alert('Cannot deactivate the default location.');
      return;
    }

    try {
      await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryLocations`, location.id), {
        isActive: !location.isActive,
        updatedAt: serverTimestamp()
      });
      invalidateMaster(activeCompany.id, COLL.locations);
      fetchLocations();
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const filteredLocations = locations.filter(loc =>
    loc.locationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    loc.locationCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (loc.city && loc.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeLocations = locations.filter(loc => loc.isActive !== false);
  const defaultLocation = locations.find(loc => loc.isDefault);

  const locationTypes = ['Warehouse', 'Store', 'Showroom', 'Factory', 'Distribution Center', 'Transit'];

  return (
    <PageLayout pageTitle="Inventory Locations" breadcrumb="Master Data / Inventory Locations">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          {canEdit && (
            <AddButton onClick={handleAdd}>
              <FiPlus /> Add Location
            </AddButton>
          )}
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiMapPin /></StatIcon>
            <StatInfo>
              <StatValue>{locations.length}</StatValue>
              <StatLabel>Total Locations</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiCheck /></StatIcon>
            <StatInfo>
              <StatValue>{activeLocations.length}</StatValue>
              <StatLabel>Active Locations</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.info.main}><FiHome /></StatIcon>
            <StatInfo>
              <StatValue>{defaultLocation?.locationName || 'Not Set'}</StatValue>
              <StatLabel>Default Location</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading locations...</LoadingMessage>
        ) : (
          <TableContainer>
            {filteredLocations.length === 0 ? (
              <EmptyMessage>
                {searchTerm ? 'No locations found matching your search.' : 'No locations found. Click "Add Location" to create one.'}
              </EmptyMessage>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Location Name</th>
                    <th>Type</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map(location => (
                    <tr key={location.id}>
                      <td>
                        <LocationCode>{location.locationCode}</LocationCode>
                      </td>
                      <td>
                        <LocationName>
                          {location.locationName}
                          {location.isDefault && <DefaultBadge>Default</DefaultBadge>}
                        </LocationName>
                      </td>
                      <td>
                        <TypeBadge $type={location.locationType}>
                          {location.locationType || 'Warehouse'}
                        </TypeBadge>
                      </td>
                      <td>{location.city || '-'}</td>
                      <td>
                        {location.contactPerson ? (
                          <ContactInfo>
                            <span>{location.contactPerson}</span>
                            {location.contactPhone && <small>{location.contactPhone}</small>}
                          </ContactInfo>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <StatusToggle
                          $active={location.isActive !== false}
                          onClick={canEdit ? () => toggleActive(location) : undefined}
                          title={canEdit ? (location.isActive !== false ? 'Click to deactivate' : 'Click to activate') : (location.isActive !== false ? 'Active' : 'Inactive')}
                          style={canEdit ? undefined : { cursor: 'default' }}
                        >
                          {location.isActive !== false ? 'Active' : 'Inactive'}
                        </StatusToggle>
                      </td>
                      <td>
                        <ActionButtons>
                          {!location.isDefault && canEdit && (
                            <ActionButton
                              onClick={() => handleSetDefault(location)}
                              title="Set as Default"
                              $primary
                            >
                              <FiHome />
                            </ActionButton>
                          )}
                          {canChange && (
                            <ActionButton onClick={() => handleEdit(location)} title="Edit">
                              <FiEdit2 />
                            </ActionButton>
                          )}
                          {canDelete && (
                            <ActionButton
                              $danger
                              onClick={() => handleDelete(location)}
                              title="Delete"
                              disabled={location.isDefault}
                            >
                              <FiTrash2 />
                            </ActionButton>
                          )}
                        </ActionButtons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>
        )}
      </Container>

      {/* Add/Edit Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingLocation ? 'Edit Location' : 'Add Location'}</h3>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormRow>
                <FormGroup>
                  <label>Location Code</label>
                  <input
                    type="text"
                    value={formData.locationCode}
                    onChange={(e) => setFormData({ ...formData, locationCode: e.target.value })}
                    disabled={editingLocation}
                  />
                </FormGroup>
                <FormGroup>
                  <label>Location Type</label>
                  <select
                    value={formData.locationType}
                    onChange={(e) => setFormData({ ...formData, locationType: e.target.value })}
                  >
                    {locationTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </FormGroup>
              </FormRow>
              <FormGroup>
                <label>Location Name *</label>
                <input
                  type="text"
                  value={formData.locationName}
                  onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                  placeholder="Enter location name"
                />
              </FormGroup>
              <FormGroup>
                <label>Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter full address"
                  rows={2}
                />
              </FormGroup>
              <FormRow>
                <FormGroup>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Contact Person</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    placeholder="Enter contact name"
                  />
                </FormGroup>
              </FormRow>
              <FormRow>
                <FormGroup>
                  <label>Contact Phone</label>
                  <input
                    type="text"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    placeholder="Enter phone number"
                  />
                </FormGroup>
                <FormGroup>
                  <CheckboxGroup>
                    <input
                      type="checkbox"
                      id="isDefault"
                      checked={formData.isDefault}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    />
                    <label htmlFor="isDefault">Set as Default Location</label>
                  </CheckboxGroup>
                </FormGroup>
              </FormRow>
              <FormGroup>
                <CheckboxGroup>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive">Active</label>
                </CheckboxGroup>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSave}>
                  {editingLocation ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 400px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: ${spacing.md};
    text-align: left;
    border-bottom: 1px solid ${colors.border.light};
  }

  th {
    background: ${colors.background.paper};
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
  }

  tbody tr:hover {
    background: ${colors.background.hover};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const LocationCode = styled.span`
  font-family: monospace;
  font-size: ${typography.fontSize.xs};
  background: ${colors.background.paper};
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
`;

const LocationName = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const DefaultBadge = styled.span`
  font-size: ${typography.fontSize.xs};
  background: ${colors.primary.main}20;
  color: ${colors.primary.main};
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const TypeBadge = styled.span`
  font-size: ${typography.fontSize.xs};
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  background: ${props => {
    switch (props.$type) {
      case 'Warehouse': return colors.info.main + '20';
      case 'Store': return colors.success.main + '20';
      case 'Showroom': return colors.warning.main + '20';
      case 'Factory': return colors.secondary.main + '20';
      case 'Distribution Center': return colors.primary.main + '20';
      default: return colors.text.secondary + '20';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'Warehouse': return colors.info.main;
      case 'Store': return colors.success.main;
      case 'Showroom': return colors.warning.main;
      case 'Factory': return colors.secondary.main;
      case 'Distribution Center': return colors.primary.main;
      default: return colors.text.secondary;
    }
  }};
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  small {
    font-size: ${typography.fontSize.xs};
    color: ${colors.text.secondary};
  }
`;

const StatusToggle = styled.button`
  font-size: ${typography.fontSize.xs};
  padding: 4px 12px;
  border-radius: ${borderRadius.full};
  background: ${props => props.$active ? colors.success.main + '20' : colors.error.main + '20'};
  color: ${props => props.$active ? colors.success.main : colors.error.main};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? colors.success.main + '30' : colors.error.main + '30'};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${props => props.$danger ? colors.error.main : props.$primary ? colors.primary.main : colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.background.hover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding-top: ${spacing.md};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    font-weight: ${typography.fontWeight.regular};
    color: ${colors.text.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

export default InventoryLocations;
